import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSsids } from "../api/portal";
import { BrandedFooter } from "./BrandedFooter";
import { detectOs, type OS } from "../utils/detectOs";
import { ConnectGuideAndroid } from "./connect-guides/ConnectGuideAndroid";
import { ConnectGuideChromeOs } from "./connect-guides/ConnectGuideChromeOs";
import { ConnectGuideIos } from "./connect-guides/ConnectGuideIos";
import { ConnectGuideLinux } from "./connect-guides/ConnectGuideLinux";
import { ConnectGuideMacos } from "./connect-guides/ConnectGuideMacos";
import { ConnectGuideWindows } from "./connect-guides/ConnectGuideWindows";

const ConnectGuides: Record<OS, React.ComponentType> = {
  ios: ConnectGuideIos,
  android: ConnectGuideAndroid,
  windows: ConnectGuideWindows,
  macos: ConnectGuideMacos,
  chromeos: ConnectGuideChromeOs,
  linux: ConnectGuideLinux,
};

const OS_OPTIONS: OS[] = ["ios", "android", "windows", "macos", "chromeos", "linux"];

interface NoWifiHelpPageProps {
  siteName: string;
  portalToken: string;
  onConnected: () => void;
  onReportProblem: () => void;
}

export function NoWifiHelpPage({
  siteName,
  portalToken,
  onConnected,
  onReportProblem,
}: NoWifiHelpPageProps) {
  const { t } = useTranslation();
  const [guideOpen, setGuideOpen] = useState(false);
  const [os, setOs] = useState<OS>(() => detectOs());
  const [ssids, setSsids] = useState<string[] | null>(null);
  const Guide = ConnectGuides[os];

  const osLabels: Record<OS, string> = {
    ios: t("os_ios"),
    android: t("os_android"),
    windows: t("os_windows"),
    macos: t("os_macos"),
    chromeos: t("os_chromeos"),
    linux: t("os_linux"),
  };

  useEffect(() => {
    let mounted = true;
    fetchSsids(portalToken)
      .then((data) => { if (mounted) setSsids(data); })
      .catch(() => { if (mounted) setSsids([]); });
    return () => { mounted = false; };
  }, [portalToken]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto px-4 py-8">
        <h1 className="text-xl font-semibold text-gray-800 mb-6">{siteName}</h1>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-4">
          <p className="px-5 pt-5 pb-4 font-semibold text-gray-800">{t("no_wifi_title")}</p>

          {/* How-to guide accordion */}
          <div className="border-t border-gray-100">
            <button
              type="button"
              onClick={() => setGuideOpen((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-blue-600 hover:bg-gray-50 transition-colors"
            >
              <span>{t("no_wifi_how_btn")}</span>
              <span className="text-gray-400">{guideOpen ? "▲" : "▼"}</span>
            </button>

            {guideOpen && (
              <div className="px-5 pb-5">
                {/* OS selector */}
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-2">
                    {t("os_detected")}: <span className="font-medium text-gray-700">{osLabels[os]}</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {OS_OPTIONS.map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => setOs(o)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          o === os
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {osLabels[o]}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Guide illustration */}
                <div className="mt-3">
                  <Guide />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* SSID list */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-6">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {ssids && ssids.length > 0 ? t("no_wifi_available") : ""}
          </p>
          {ssids === null ? (
            <p className="text-sm text-gray-400">{t("no_wifi_loading")}</p>
          ) : ssids.length > 0 ? (
            <ul className="space-y-1">
              {ssids.map((ssid) => (
                <li key={ssid} className="text-sm text-gray-800 flex items-center gap-2">
                  <span className="text-blue-500">📶</span> {ssid}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">
              {t("no_wifi_available_fallback")}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={onConnected}
            className="w-full py-3 px-4 btn-primary rounded-xl font-semibold transition-colors"
          >
            ✓ {t("no_wifi_connected")}
          </button>
          <button
            type="button"
            onClick={onReportProblem}
            className="w-full py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
          >
            {t("no_wifi_report")}
          </button>
        </div>

        <BrandedFooter />
      </div>
    </div>
  );
}
